/**
 * Client-side JavaScript: Core Rendering.
 * Utilities, data transformation, summary, test grid, drawer, and artifacts.
 */

import { VERDICT_OVERRIDES, EXPECTATION_NOTES } from './expected-status.js';

export const JS_RENDER = `
  /* ── Utilities ── */
  function esc(s){if(!s)return '';return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;')}
  function stripAnsi(s){return s?s.replace(/\\u001b\\[[0-9;]*m/g,'').replace(/\\x1b\\[[0-9;]*m/g,''):''}
  function fmtDur(ms){if(ms<1000)return Math.round(ms)+'ms';if(ms<60000)return (ms/1000).toFixed(1)+'s';var m=Math.floor(ms/60000),s=Math.round((ms%60000)/1000);return s>0?m+'m '+s+'s':m+'m'}
  function fmtBytes(b){if(b===0)return '0 B';if(b<1024)return b+' B';if(b<1048576)return (b/1024).toFixed(1)+' KB';return (b/1048576).toFixed(1)+' MB'}
  function fmtDate(iso){try{return new Date(iso).toLocaleString()}catch(e){return iso}}
  function shortTitle(t){var p=t.split(' > ');return p.length>1?p[p.length-1]:t}

  /* ── Playwright's verdict ──
     The status on an index row or a timeline entry is the actual result.
     Playwright counts a result as expected only when it equals the test's
     expected status, so a test.fail() that failed is a pass and one that
     passed is a failure. The tables are expected-status.ts's own,
     interpolated as JSON data (not code), so the report and the terminal box
     cannot disagree about a pair. Defined before the data transformation
     below, which is their first caller. */
  var PW_VERDICT=${JSON.stringify(VERDICT_OVERRIDES)};var PW_NOTE=${JSON.stringify(EXPECTATION_NOTES)};
  function pwKey(s,e){return (e||'passed')+'|'+s;}
  function pwVerdict(s,e){return PW_VERDICT[pwKey(s,e)]||s;}
  function pwNote(s,e){return PW_NOTE[pwKey(s,e)]||null;}

  /* ── Data Transformation ── */
  var testMap={};
  for(var i=0;i<data.timeline.length;i++){
    var entry=data.timeline[i];
    for(var j=0;j<entry.tests.length;j++){
      var t=entry.tests[j];
      /* Known and not fixed here: this keeps the FIRST attempt the timeline
         lists, so a retried test's row can show an earlier attempt's status
         while the verdict band counts final attempts. */
      if(!testMap[t.testId]){
        /* status is the verdict every surface reads (colour, filters, needs
           attention); rawStatus keeps the actual result for the detail notes. */
        testMap[t.testId]={
          testId:t.testId,title:t.title,status:pwVerdict(t.status,t.expectedStatus),rawStatus:t.status,duration:t.duration,
          startedAt:t.startedAt,completedAt:t.completedAt,retryCount:t.retryCount,
          tags:t.tags,failure:t.failure,filePath:t.filePath,suiteName:t.suiteName,
          testType:t.testType,isFlaky:t.isFlaky,retryStatus:t.retryStatus,
          expectedStatus:t.expectedStatus,actualStatus:t.actualStatus,
          artifacts:t.artifacts,visual:t.visual||[],networkRequests:t.networkRequests||[],steps:[],apiCalls:[],actions:t.actions||[],consoleLogs:t.consoleLogs||[],
          apiAssertions:t.apiAssertions||[],
          apiCallCount:0,apiStatusMix:{'2xx':0,'3xx':0,'4xx':0,'5xx':0,'error':0},
          assertionsPassed:0,assertionsFailed:0,
          device:t.device||null,aiDefects:t.aiDefects||[]
        };
        var _as=testMap[t.testId].apiAssertions;
        for(var _ai=0;_ai<_as.length;_ai++){
          if(_as[_ai].status==='failed')testMap[t.testId].assertionsFailed++;
          else testMap[t.testId].assertionsPassed++;
        }
      }
      if(entry.type==='api_call'){
        var _mt=testMap[t.testId];
        _mt.apiCallCount++;
        var _code=entry.response&&typeof entry.response.status==='number'?entry.response.status:null;
        var _bucket=_code===null?'error':(_code>=500?'5xx':(_code>=400?'4xx':(_code>=300?'3xx':(_code>=200?'2xx':'error'))));
        _mt.apiStatusMix[_bucket]++;
        _mt.apiCalls.push({
          callId:entry.callId,method:entry.method,url:entry.url,
          responseTime:entry.responseTime,timestamp:entry.timestamp,
          request:entry.request,response:entry.response,assertions:entry.assertions
        });
      }else{
        testMap[t.testId].steps.push({
          url:entry.url,navigationType:entry.navigationType,
          visitedAt:entry.visitedAt||entry.timestamp,duration:entry.duration||entry.durationOnUrl||0,specFile:entry.specFile,
          domContentLoadedAt:entry.domContentLoadedAt,networkIdleAt:entry.networkIdleAt,
          networkStats:entry.networkStats
        });
      }
    }
  }
  var tests=[];
  for(var id in testMap){if(testMap.hasOwnProperty(id))tests.push(testMap[id])}
  tests.sort(function(a,b){
    if(a.filePath!==b.filePath)return a.filePath<b.filePath?-1:1;
    return a.startedAt<b.startedAt?-1:1;
  });

  /* ── Streaming mode: build tests from compact index, grouping retries under the final attempt ── */
  if(data.reportMode==='streaming'&&data.index&&data.index.length>0){
    tests=[];testMap={};
    var _retryGroups={};
    for(var xi=0;xi<data.index.length;xi++){
      var ix=data.index[xi];
      var gk=ix.retryGroupKey||ix.id;
      if(!_retryGroups[gk])_retryGroups[gk]={final:null,attempts:[]};
      if(ix.isRetry){_retryGroups[gk].attempts.push(ix);}
      else{_retryGroups[gk].final=ix;}
    }
    for(var gk2 in _retryGroups){
      if(!_retryGroups.hasOwnProperty(gk2))continue;
      var grp=_retryGroups[gk2];
      // Defensive: if every attempt in a group was marked isRetry (shouldn't happen),
      // fall back to the highest retryIndex so the test still surfaces.
      var final=grp.final;
      if(!final&&grp.attempts.length>0){
        final=grp.attempts.reduce(function(a,b){return b.retryIndex>a.retryIndex?b:a;});
        grp.attempts=grp.attempts.filter(function(a){return a!==final;});
      }
      if(!final)continue;
      var ix2=final;
      /* The row carries expectedStatus since #135; it was thrown away here,
         so a test.fail() that failed as expected rendered as a failure. */
      var st={
        testId:ix2.id,title:ix2.title,status:pwVerdict(ix2.status,ix2.expectedStatus),rawStatus:ix2.status,duration:ix2.duration,
        filePath:ix2.filePath,tags:ix2.tags||[],isFlaky:ix2.status==='flaky',
        testType:ix2.testType||'e2e',retryCount:grp.attempts.length,suiteName:'',
        startedAt:'',completedAt:'',
        networkRequests:[],steps:[],apiCalls:[],actions:[],consoleLogs:[],
        artifacts:null,failure:null,retryStatus:null,expectedStatus:ix2.expectedStatus||'passed',actualStatus:null,
        hasNetworkData:ix2.hasNetworkData,hasConsoleData:ix2.hasConsoleData,
        hasActionSteps:ix2.hasActionSteps,hasArtifacts:ix2.hasArtifacts,
        errorMessage:ix2.errorMessage,networkCount:ix2.networkCount||0,
        consoleCount:ix2.consoleCount||0,actionCount:ix2.actionCount||0,
        /* Carried from the index so the grid and the filter bar can count
           visual comparisons without opening every test's detail file. */
        visualCount:ix2.visualCount||0,visualFailedCount:ix2.visualFailedCount||0,
        domChangeCount:ix2.domChangeCount||0,visualDiffPath:ix2.visualDiffPath||null,
        /* API and mobile evidence, same rule: the grid must never open a
           test's detail file to draw a row. */
        apiCallCount:ix2.apiCallCount||0,apiStatusMix:ix2.apiStatusMix||null,
        assertionsPassed:ix2.assertionsPassed||0,assertionsFailed:ix2.assertionsFailed||0,
        firstFailedAssertion:ix2.firstFailedAssertion||null,
        device:ix2.device||null,stepKinds:ix2.stepKinds||null,
        deviceLogErrors:ix2.deviceLogErrors||0,hasCrashLog:!!ix2.hasCrashLog,
        hasVideo:!!ix2.hasVideo,aiDefectCount:ix2.aiDefectCount||0,
        retryAttempts:grp.attempts.slice().sort(function(a,b){return a.retryIndex-b.retryIndex;}).map(function(a){
          return {testId:a.id,retryIndex:a.retryIndex,status:pwVerdict(a.status,a.expectedStatus),rawStatus:a.status,expectedStatus:a.expectedStatus||'passed',duration:a.duration,errorMessage:a.errorMessage};
        })
      };
      testMap[st.testId]=st;
      tests.push(st);
      // Register each retry attempt in testMap too (keyed by its own id) so opening
      // its drawer has a title/meta to merge with the fetched detail — it never
      // appears in the grid itself, only as a nested row under the final attempt.
      for(var ai=0;ai<st.retryAttempts.length;ai++){
        var ra=st.retryAttempts[ai];
        testMap[ra.testId]={
          testId:ra.testId,title:st.title+' (attempt '+(ra.retryIndex+1)+')',status:ra.status,rawStatus:ra.rawStatus,
          duration:ra.duration,filePath:st.filePath,tags:st.tags,isFlaky:false,
          testType:st.testType,retryCount:0,suiteName:'',startedAt:'',completedAt:'',
          networkRequests:[],steps:[],apiCalls:[],actions:[],consoleLogs:[],
          artifacts:null,failure:null,retryStatus:'attempt '+(ra.retryIndex+1)+' of '+(st.retryAttempts.length+1),
          expectedStatus:ra.expectedStatus,actualStatus:null,errorMessage:ra.errorMessage,
          networkCount:0,consoleCount:0,actionCount:0
        };
      }
    }
    tests.sort(function(a,b){
      if(a.filePath!==b.filePath)return a.filePath<b.filePath?-1:1;
      return 0;
    });
  }

  /* ── Streaming detail cache (LRU, max 50 entries) ── */
  var _detailCache={};
  var _detailCacheKeys=[];
  var _detailCacheMax=50;
  function cacheDetail(testId,data){
    if(_detailCache[testId]){
      var ki=_detailCacheKeys.indexOf(testId);
      if(ki>-1)_detailCacheKeys.splice(ki,1);
    }else if(_detailCacheKeys.length>=_detailCacheMax){
      var evict=_detailCacheKeys.shift();
      delete _detailCache[evict];
    }
    _detailCache[testId]=data;
    _detailCacheKeys.push(testId);
  }

  /* ── State ── */
  var searchQuery='';

  /* ── DOM Refs ── */
  var runMetaEl=document.getElementById('run-meta');
  var summaryEl=document.getElementById('summary-strip');
  var filterEl=document.getElementById('filter-bar');
  var testGridEl=document.getElementById('test-grid');
  var drawerBodyEl=document.getElementById('drawer-body');

  /* ── Render: Run Meta ── */
  function renderRunMeta(){
    var h='';
    h+='<div class="run-meta-item"><span class="run-meta-label">Run</span><span class="run-id-tag">'+esc(data.testRunId.substring(0,8))+'</span></div>';
    h+='<div class="run-meta-item"><span class="run-meta-label">Duration</span>'+fmtDur(data.totalDuration)+'</div>';
    h+='<div class="run-meta-item">'+fmtDate(data.startedAt)+'</div>';
    if(data.ci){
      h+='<div class="run-meta-item"><span class="ci-tag">'+esc(data.ci.provider)+'</span></div>';
      if(data.ci.branch)h+='<div class="run-meta-item"><span class="run-meta-label">Branch</span>'+esc(data.ci.branch)+'</div>';
      if(data.ci.commitSha)h+='<div class="run-meta-item"><span class="run-meta-label">Commit</span><span class="run-id-tag">'+esc(data.ci.commitSha.substring(0,8))+'</span></div>';
    }
    if(data.shardRunIds&&data.shardRunIds.length>0){
      h+='<div class="run-meta-item"><span class="merged-tag">Merged ('+data.shardRunIds.length+' shards)</span></div>';
    }
    runMetaEl.innerHTML=h;
  }

  /* ── Render: Summary ── */
  /* renderSummary lives in html-js-layout: the six equal stat chips were
     replaced by the verdict band, which collapses empty statuses. */

  /* Longest test in the run, so each bar reads as "relative to the slowest".
     Recomputed per grid render because filtering changes the set. */
  var _maxDur=1;
  function rowBarPct(d){
    var pct=Math.round(((d||0)/_maxDur)*100);
    /* A bar too short to see says nothing; floor it at something visible. */
    return Math.max(4,Math.min(100,pct));
  }

  /* Small square icons: what was captured, before you open the test. */

  /* Both report modes reach these renderers with different shapes: the
     streaming index carries counters, the embedded testMap carries the arrays.
     Reading only the counters made the evidence column, the needs-attention
     chips and the diff thumbnail vanish in embedded mode. */
  function evVisualCount(t){return t.visual?t.visual.length:(t.visualCount||0);}
  function evVisualFailed(t){
    if(t.visual){var n=0;for(var i=0;i<t.visual.length;i++)if(t.visual[i].status==='failed')n++;return n;}
    return t.visualFailedCount||0;
  }
  function evDomChanges(t){
    if(t.visual){var n=0;for(var i=0;i<t.visual.length;i++)n+=(t.visual[i].domChangeCount||0);return n;}
    return t.domChangeCount||0;
  }
  function evHasArtifacts(t){
    if(t.artifacts)return !!(t.artifacts.screenshot||t.artifacts.video||t.artifacts.trace);
    return !!t.hasArtifacts;
  }
  function evNetworkCount(t){return t.networkRequests?t.networkRequests.length:(t.networkCount||0);}
  function evConsoleCount(t){return t.consoleLogs?t.consoleLogs.length:(t.consoleCount||0);}
  function evDiffPath(t){
    if(t.visualDiffPath)return t.visualDiffPath;
    if(t.visual){for(var i=0;i<t.visual.length;i++){if(t.visual[i].diff)return t.visual[i].diff;}}
    return null;
  }

  function rowEvidence(t){
    var h='<div class="trow-evidence">';
    var any=false;
    if(typeof apiRowEvidence==='function'){
      var _ae=apiRowEvidence(t);
      if(_ae){h+=_ae;any=true;}
    }
    if(typeof mobileRowEvidence==='function'){
      var _me=mobileRowEvidence(t);
      if(_me){h+=_me;any=true;}
    }
    var _vis=evVisualCount(t),_visFail=evVisualFailed(t),_dom=evDomChanges(t);
    var _net=evNetworkCount(t),_con=evConsoleCount(t);
    if(_vis>0){
      any=true;
      h+='<span class="tev'+(_visFail>0?' tev-visual':'')+'" title="'+(_visFail>0?'Visual comparison failed':'Visual comparison')+'"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 3v18"/></svg></span>';
    }
    if(_dom>0){
      any=true;
      h+='<span class="tev tev-dom" title="'+_dom+' DOM elements changed"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H5a2 2 0 00-2 2v3M16 3h3a2 2 0 012 2v3M8 21H5a2 2 0 01-2-2v-3M16 21h3a2 2 0 002-2v-3"/></svg></span>';
    }
    if(evHasArtifacts(t)){
      any=true;
      h+='<span class="tev" title="Video and trace"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21"/></svg></span>';
    }
    if(_net>0){
      any=true;
      h+='<span class="tev" title="'+_net+' network requests"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12h4l3 8 4-16 3 8h4"/></svg></span>';
    }
    if(_con>0){
      any=true;
      h+='<span class="tev" title="'+_con+' console messages"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 17l6-6-6-6M12 19h8"/></svg></span>';
    }
    /* Keep the column even when empty so the rows stay aligned. */
    if(!any)h+='<span style="width:18px"></span>';
    return h+'</div>';
  }

  /* ── Virtual Grid: progressive chunked rendering ── */
  var _vgGroups=[];
  var _vgRenderedTo=0;
  var _vgChunkSize=10;
  var _vgSentinel=null;
  var _vgObserver=null;

  function _vgBuildGroups(filtered){
    var groups=[];var lastFile='';var g=null;
    for(var i=0;i<filtered.length;i++){
      var t=filtered[i];
      if(t.filePath!==lastFile){
        g={filePath:t.filePath,tests:[]};
        groups.push(g);
        lastFile=t.filePath;
      }
      g.tests.push(t);
    }
    return groups;
  }

  function _vgRenderGroupHtml(g){
    var h='<div class="file-group">';
    h+='<div class="file-group-header">'+esc(g.filePath)+'</div>';
    h+='<div class="file-group-card">';
    for(var i=0;i<g.tests.length;i++){
      var t=g.tests[i];
      h+='<div class="test-row" data-testid="'+esc(t.testId)+'">';
      h+='<div class="status-indicator si-'+t.status+'"></div>';
      h+='<div class="test-row-info tri-inline">';
      h+='<div class="test-row-title" title="'+esc(t.title)+'">'+esc(shortTitle(t.title))+'</div>';
      var hasBadges=(t.testType&&t.testType!=='unknown')||t.isFlaky||(t.tags&&t.tags.length>0)||t.expectedStatus==='failed';
      if(hasBadges){
        h+='<div class="test-row-badges">';
        if(t.testType&&t.testType!=='unknown')h+='<span class="trb trb-type">'+esc(t.testType)+'</span>';
        if(t.isFlaky)h+='<span class="trb trb-flaky">flaky</span>';
        /* A green row whose test failed needs saying so, or it reads as a bug. */
        if(t.expectedStatus==='failed')h+='<span class="trb trb-xfail" title="test.fail(): Playwright expects this test to fail">expected to fail</span>';
        if(t.retryCount>0)h+='<span class="trb trb-retry">'+t.retryCount+' retries</span>';
        if(t.tags){for(var ti=0;ti<t.tags.length;ti++){if(t.tags[ti])h+='<span class="trb trb-tag">'+esc(t.tags[ti])+'</span>';}}
        h+='</div>';
      }
      h+='</div>';
      h+=rowEvidence(t);
      h+='<div class="trow-time"><div class="trow-bar"><span class="tb-'+t.status+'" style="width:'+rowBarPct(t.duration)+'%"></span></div>';
      h+='<span class="test-row-dur">'+fmtDur(t.duration)+'</span></div>';
      h+='<svg class="test-row-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
      h+='</div>';
      if(t.retryAttempts&&t.retryAttempts.length>0){
        h+='<div class="test-row-retries">';
        for(var ri=0;ri<t.retryAttempts.length;ri++){
          var ra=t.retryAttempts[ri];
          h+='<div class="test-row test-row-retry-item" data-testid="'+esc(ra.testId)+'">';
          h+='<div class="status-indicator si-'+ra.status+'"></div>';
          var _raNote=ra.errorMessage?null:pwNote(ra.rawStatus,ra.expectedStatus);
          h+='<div class="test-row-info"><div class="test-row-title">Attempt '+(ra.retryIndex+1)+' &mdash; '+esc(ra.status)+(ra.errorMessage?': '+esc(ra.errorMessage):(_raNote?': '+esc(_raNote):''))+'</div></div>';
          h+='<span class="test-row-dur">'+fmtDur(ra.duration)+'</span>';
          h+='<svg class="test-row-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
          h+='</div>';
        }
        h+='</div>';
      }
    }
    h+='</div></div>';
    return h;
  }

  function _vgRenderChunk(){
    if(_vgRenderedTo>=_vgGroups.length){
      if(_vgSentinel)_vgSentinel.style.display='none';
      return;
    }
    var end=Math.min(_vgRenderedTo+_vgChunkSize,_vgGroups.length);
    var frag=document.createDocumentFragment();
    var tmp=document.createElement('div');
    var h='';
    for(var i=_vgRenderedTo;i<end;i++){h+=_vgRenderGroupHtml(_vgGroups[i]);}
    tmp.innerHTML=h;
    while(tmp.firstChild)frag.appendChild(tmp.firstChild);
    /* Insert before sentinel */
    if(_vgSentinel&&_vgSentinel.parentNode){
      _vgSentinel.parentNode.insertBefore(frag,_vgSentinel);
    }
    _vgRenderedTo=end;
    if(_vgRenderedTo>=_vgGroups.length&&_vgSentinel){
      _vgSentinel.style.display='none';
    }
  }

  function _vgSetupObserver(){
    if(_vgObserver){_vgObserver.disconnect();_vgObserver=null;}
    if(!_vgSentinel)return;
    if(typeof IntersectionObserver==='undefined'){
      /* Fallback: render everything */
      while(_vgRenderedTo<_vgGroups.length)_vgRenderChunk();
      return;
    }
    _vgObserver=new IntersectionObserver(function(entries){
      if(entries[0].isIntersecting)_vgRenderChunk();
    },{rootMargin:'600px'});
    _vgObserver.observe(_vgSentinel);
  }

  /* ── Render: Test Grid ── */
  function renderTestGrid(){
    if(_vgObserver){_vgObserver.disconnect();_vgObserver=null;}
    var filtered=getFilteredTests();
    /* The bar on each row is relative to the slowest test CURRENTLY SHOWN, so
       filtering to the slow ones still spreads them across the full width. */
    _maxDur=1;
    for(var md=0;md<filtered.length;md++){if(filtered[md].duration>_maxDur)_maxDur=filtered[md].duration;}
    if(filtered.length===0){testGridEl.innerHTML='<div class="no-tests">No tests match your filters</div>';_vgGroups=[];return;}
    _vgGroups=_vgBuildGroups(filtered);
    _vgRenderedTo=0;
    /* Render initial batch directly, then progressively load the rest */
    var initCount=Math.min(_vgChunkSize,_vgGroups.length);
    var h='';
    for(var i=0;i<initCount;i++){h+=_vgRenderGroupHtml(_vgGroups[i]);}
    _vgRenderedTo=initCount;
    h+='<div class="vg-sentinel" id="vg-sentinel"></div>';
    testGridEl.innerHTML=h;
    _vgSentinel=document.getElementById('vg-sentinel');
    if(_vgRenderedTo<_vgGroups.length){
      _vgSetupObserver();
    }else if(_vgSentinel){
      _vgSentinel.style.display='none';
    }
  }

  /* ── Render helpers (code, artifacts) ── */

  function renderCodeSnippet(code){
    var lines=code.split('\\n');var out='<div class="code-snippet">';
    for(var k=0;k<lines.length;k++){var raw=lines[k];var numMatch=raw.match(/^\\s*(>?\\s*)(\\d+)\\s*\\|/);var lineNum=numMatch?numMatch[2]:'';var isHighlight=raw.trimStart().startsWith('>');var codePart=raw.replace(/^\\s*>?\\s*\\d+\\s*\\|/,'');out+='<div class="code-line'+(isHighlight?' highlight':'')+'"><span class="line-num">'+esc(lineNum)+'</span><span class="line-code">'+esc(codePart)+'</span></div>';}
    out+='</div>';return out;
  }

  function renderArtifactColumn(artifacts){
    if(!artifacts)return '<div class="artifact-empty">No artifacts captured</div>';
    var hasS=!!artifacts.screenshot,hasV=!!artifacts.video,hasT=!!artifacts.trace;
    if(!hasS&&!hasV&&!hasT)return '<div class="artifact-empty">No artifacts captured</div>';
    var tabs=[];
    if(hasS)tabs.push('screenshot');
    if(hasV)tabs.push('video');
    if(hasT)tabs.push('trace');

    var h='';
    if(tabs.length>1){
      h+='<div class="seg-ctrl" data-seg-group="artifacts">';
      for(var ti=0;ti<tabs.length;ti++){
        var t=tabs[ti],ico='';
        if(t==='screenshot')ico='<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>';
        else if(t==='video')ico='<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21"/></svg>';
        else ico='<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16v6H4zM4 14h16v6H4z"/></svg>';
        var label=t==='screenshot'?'Screenshot':(t==='video'?'Video':'Trace');
        h+='<button class="seg-btn'+(ti===0?' active':'')+'" data-seg="'+t+'">'+ico+label+'</button>';
      }
      h+='</div>';
    }

    /* No lazy loading. A lazily-loaded image inside a display:none pane is
       never fetched by Chrome, and every pane but the first starts hidden —
       the screenshot simply never appeared. The same bug was fixed for the
       visual comparison images and left standing here. */
    if(hasS){
      h+='<div class="seg-pane'+(tabs[0]==='screenshot'?' active':'')+'" data-seg-pane="screenshot">';
      h+='<img class="artifact-col-img" src="'+esc(artifacts.screenshot)+'" alt="Screenshot">';
      h+='<div class="artifact-col-caption"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>Click to enlarge</div></div>';
    }
    if(hasV){
      h+='<div class="seg-pane'+(tabs[0]==='video'?' active':'')+'" data-seg-pane="video">';
      h+='<div class="artifact-col-video"><video controls preload="metadata" src="'+esc(artifacts.video)+'"></video></div>';
      h+='<div class="artifact-col-caption">'+esc(artifacts.video.split('/').pop())+'</div></div>';
    }
    /* The trace is the thing a failure is actually debugged from, and the
       report had no way to reach it even though it was captured and copied. */
    if(hasT){
      h+='<div class="seg-pane'+(tabs[0]==='trace'?' active':'')+'" data-seg-pane="trace">';
      h+='<div class="trace-open"><a class="trace-link" href="'+esc(artifacts.trace)+'" download>';
      h+='<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v12M7 10l5 5 5-5M5 21h14"/></svg>';
      h+='Download '+esc(artifacts.trace.split('/').pop())+'</a>';
      h+='<div class="trace-hint">Open it with <code>npx playwright show-trace &lt;file&gt;</code>, or drop it on <code>trace.playwright.dev</code>.</div>';
      h+='</div></div>';
    }
    return h;
  }

  /* ── Render: Drawer Content ── */
  function renderDrawer(testId,detail){
    var t=detail||testMap[testId];
    if(!t){drawerBodyEl.innerHTML='';return;}
    /* t.status is the verdict; _raw is what actually happened, which decides
       whether there is an error to show. */
    var _raw=t.rawStatus||t.status,_note=pwNote(_raw,t.expectedStatus);
    var h='<div class="test-detail">';

    /* ── Top: Metadata ── */
    h+='<div class="test-detail-header"><div class="test-detail-top">';
    h+='<span class="detail-status-badge dbg-'+t.status+'">'+t.status+'</span>';
    h+='<div class="detail-title-section"><div class="detail-title">'+esc(t.title)+'</div><div class="detail-meta">';
    if(t.testType&&t.testType!=='unknown')h+='<span class="dm-badge dm-type">'+esc(t.testType)+'</span>';
    if(t.isFlaky)h+='<span class="dm-badge dm-flaky">Flaky</span>';
    if(t.retryCount>0)h+='<span class="dm-badge dm-retry">'+t.retryCount+' retries</span>';
    if(t.retryStatus)h+='<span class="dm-badge dm-retry">'+esc(t.retryStatus)+'</span>';
    if(t.tags&&t.tags.length>0){for(var ti=0;ti<t.tags.length;ti++){if(t.tags[ti])h+='<span class="dm-badge dm-tag">'+esc(t.tags[ti])+'</span>';}}
    if(_note)h+='<span class="dm-badge '+(t.status==='passed'?'dm-xfail':'dm-xfail-bad')+'">'+esc(_note)+'</span>';
    else if(t.expectedStatus&&t.actualStatus&&t.expectedStatus!==t.actualStatus)h+='<span class="dm-badge" style="background:rgba(239,68,68,0.12);color:#ef4444">expected: '+esc(t.expectedStatus)+' actual: '+esc(t.actualStatus)+'</span>';
    h+='</div>';
    if(t.testId||t.filePath||t.suiteName){h+='<div class="detail-sub-meta">';if(t.testId)h+='<span><span class="meta-k">ID</span><span class="detail-id" title="'+esc(t.testId)+'">'+esc(t.testId.substring(0,12))+'</span></span>';if(t.filePath)h+='<span><span class="meta-k">File</span><span class="detail-file">'+esc(t.filePath)+'</span></span>';if(t.suiteName)h+='<span><span class="meta-k">Suite</span><span class="detail-suite">'+esc(t.suiteName)+'</span></span>';h+='</div>';}
    h+='</div><div class="detail-duration">'+fmtDur(t.duration)+'</div></div></div>';

    /* ── Failure panel (full width) ── */
    /* ── Stacked sections: 1) Artifacts  2) Timeline / Network  3) API Calls ── */
    var hasArt=t.artifacts&&(t.artifacts.screenshot||t.artifacts.video||t.artifacts.trace);
    var hasVisual=t.visual&&t.visual.length>0;
    var hasSteps=t.steps&&t.steps.length>0;
    var hasApiCalls=t.apiCalls&&t.apiCalls.length>0;
    var hasActions=t.actions&&t.actions.length>0;
    var hasConsoleLogs=t.consoleLogs&&t.consoleLogs.length>0;
    var hasNetworkReqs=t.networkRequests&&t.networkRequests.length>0;
    var hasVideo=!!(t.artifacts&&t.artifacts.video);
    /* Compute base time for video offset calculation (console/network) */
    var baseTime=0;
    if(hasVideo&&t.startedAt){baseTime=Date.parse(t.startedAt);}
    if(!baseTime&&hasVideo&&t.consoleLogs&&t.consoleLogs.length>0&&t.consoleLogs[0].timestamp){baseTime=Date.parse(t.consoleLogs[0].timestamp);}

    /* From the actual result: an expected failure keeps its error, as it does
       in Playwright's own report, under an 'Expected failure' rail entry. */
    var hasFailure=(_raw==='failed'||_raw==='flaky')&&t.failure;
    var hasDom=false;
    if(t.visual){for(var dv=0;dv<t.visual.length;dv++){if(t.visual[dv].domChangeCount)hasDom=true;}}

    /* What the rail is allowed to offer — built from what actually rendered,
       so a jump can never land on a section that is not there. */
    var present={};
    if(hasFailure)present.failure={xfail:t.status==='passed'};
    else if(_note&&t.status==='failed')present.failure={};
    if(hasVisual)present.visual={n:t.visual.length};
    if(hasDom)present.dom={n:(function(){var n=0;for(var q=0;q<t.visual.length;q++)n+=t.visual[q].domChangeCount||0;return n;})()};
    if(hasArt)present.artifacts={};
    if(hasActions)present.actions={n:t.actions.length};
    if(hasSteps)present.timeline={n:t.steps.length};
    var _asserts=t.apiAssertions||[];
    var hasAssertions=_asserts.length>0;
    if(hasAssertions){
      var _af=0;
      for(var _q=0;_q<_asserts.length;_q++)if(_asserts[_q].status==='failed')_af++;
      present.assertions={n:_asserts.length,bad:_af>0};
    }
    if(hasApiCalls)present.api={n:t.apiCalls.length};
    if(!hasSteps&&hasConsoleLogs)present.console={n:t.consoleLogs.length};
    if(!hasSteps&&hasNetworkReqs)present.network={n:t.networkRequests.length};

    var anySection=hasArt||hasVisual||hasSteps||hasApiCalls||hasActions||hasConsoleLogs||hasNetworkReqs||hasAssertions;

    h+='<div class="drawer-layout">';
    h+='<div class="drawer-rail">'+renderDrawerRail(present,t)+'</div>';
    h+='<div class="drawer-main">';

    if(hasFailure)h+='<div class="drawer-section" data-section="failure">'+renderFailure(t.failure,t.filePath,t.title,t.tags)+'</div>';
    /* A test.fail() that passed has no error of its own; the failure is the pass. */
    else if(_note&&t.status==='failed')h+='<div class="drawer-section" data-section="failure"><div class="failure-panel"><div class="failure-message">'+esc(_note)+'</div></div></div>';
    else if(t.status==='failed')h+='<div class="drawer-section" data-section="failure" style="padding:14px;background:var(--bg-2);border:1px solid var(--bd);border-radius:10px;font-size:12px;color:var(--fg-2);font-style:italic">No diagnostic information available</div>';

    if(anySection){
      h+='<div class="drawer-sections">';
      if(hasVisual)h+='<div class="drawer-section" data-section="visual">'+renderVisualSection(t.visual)+'</div>';
      if(hasArt)h+='<div class="drawer-section" data-section="artifacts">'+renderArtifactColumn(t.artifacts)+'</div>';
      if(hasActions)h+='<div class="drawer-section" data-section="actions">'+renderActionsColumn(t.actions,hasVideo)+'</div>';
      if(hasSteps){var _pgMeta=t._networkTotal?{testId:t._testId,page:t._networkPage||1,total:t._networkTotal,pageSize:t._networkPageSize||500}:null;h+='<div class="drawer-section" data-section="timeline">'+renderTimelineColumn(t.steps,t.networkRequests,t.consoleLogs,t.testType,hasVideo,baseTime,_pgMeta)+'</div>';}
      if(!hasSteps&&hasNetworkReqs&&typeof renderNetworkDevTools==='function'){var _pgMeta2=t._networkTotal?{testId:t._testId,page:t._networkPage||1,total:t._networkTotal,pageSize:t._networkPageSize||500}:null;h+='<div class="drawer-section" data-section="network">'+renderNetworkDevTools(t.networkRequests,false,0,_pgMeta2)+'</div>';}
      if(!hasSteps&&hasConsoleLogs)h+='<div class="drawer-section" data-section="console">'+renderConsoleColumn(t.consoleLogs,t.testType,hasVideo,baseTime)+'</div>';
      /* Assertions before the calls: the assertion is what failed, the call
         is only where it happened. */
      if(hasAssertions&&typeof renderAssertionsSection==='function')
        h+='<div class="drawer-section" data-section="assertions">'+renderAssertionsSection(_asserts,t.apiCalls)+'</div>';
      if(hasApiCalls)h+='<div class="drawer-section" data-section="api">'+renderApiCallsColumn(t.apiCalls,_asserts)+'</div>';
      h+='</div>';
    }

    h+='</div></div>';

    h+='</div>';
    drawerBodyEl.innerHTML=h;
    /* Wire up video ↔ action step sync if both are present */
    if(typeof syncActionsWithVideo==='function')syncActionsWithVideo();
  }

  /* ── Drawer open / close ── */
  /* Fetch (streaming) and render one test into the detail view. The routing
     around it lives in html-js-layout; this only fills the page. */
  function loadAndRenderTest(testId){
    if(data.reportMode==='streaming'){
      if(_detailCache[testId]){renderDrawer(testId,_detailCache[testId]);return;}
      drawerBodyEl.innerHTML='<div class="detail-loading"><div class="loading-spinner"></div><div class="loading-text">Loading test details...</div></div>';
      if(window.__trStreamingFetchDetail){
        window.__trStreamingFetchDetail(testId,function(err,detail){
          if(err){
            var t=testMap[testId];
            var emsg=t&&t.errorMessage?'<div class="failure-message">'+esc(t.errorMessage)+'</div>':'';
            drawerBodyEl.innerHTML='<div class="detail-error">'+emsg+'<p>Server unavailable &mdash; run <code>npx testrelic serve &lt;path&gt;</code> to view full details</p></div>';
            return;
          }
          /* Merge index entry with server detail metadata */
          var idx=testMap[testId]||{};
          var merged={};
          var k;for(k in idx){if(idx.hasOwnProperty(k))merged[k]=idx[k];}
          merged.actions=detail.actions||[];
          merged.apiAssertions=detail.apiAssertions||[];
          merged.artifacts=detail.artifacts||null;
          merged.visual=detail.visual||[];
          merged.failure=detail.failureDiagnostic||detail.failure||null;
          merged.steps=detail.navigations||[];
          if(detail.startedAt)merged.startedAt=detail.startedAt;
          /* Mark counts for lazy loading */
          merged._networkCount=detail.networkRequestsCount||idx.networkCount||0;
          merged._consoleCount=detail.consoleLogsCount||idx.consoleCount||0;
          merged._apiCallsCount=detail.apiCallsCount||0;
          merged._testId=testId;
          /* Initially empty — loaded on demand */
          merged.networkRequests=[];
          merged.consoleLogs=[];
          merged.apiCalls=[];
          cacheDetail(testId,merged);
          renderDrawer(testId,merged);
          /* Lazy-load heavy data once the page is showing */
          loadHeavyData(testId,merged);
        });
      }else{
        var t=testMap[testId];
        var emsg=t&&t.errorMessage?'<div class="failure-message">'+esc(t.errorMessage)+'</div>':'';
        drawerBodyEl.innerHTML='<div class="detail-error">'+emsg+'<p>Server unavailable &mdash; run <code>npx testrelic serve &lt;path&gt;</code> to view full details</p></div>';
      }
      return;
    }
    renderDrawer(testId);
  }

  /* Lazy-load heavy data (network, console, API calls) after drawer renders */
  function loadHeavyData(testId,merged){
    var fetchData=window.__trFetchTestData;
    if(!fetchData)return;
    var pending=0;
    var loaded=0;
    function onLoaded(){loaded++;if(loaded>=pending){renderDrawer(testId,merged);}}
    /* Load first page of network requests */
    if(merged._networkCount>0){
      pending++;
      fetchData(testId,'network',1,500,function(err,result){
        if(!err&&result){
          merged.networkRequests=result.items;
          merged._networkTotal=result.total;
          merged._networkPage=1;
          merged._networkPageSize=result.pageSize;
          merged._networkLoading=false;
        }
        onLoaded();
      });
    }
    /* Load first page of console logs */
    if(merged._consoleCount>0){
      pending++;
      fetchData(testId,'console',1,500,function(err,result){
        if(!err&&result){
          merged.consoleLogs=result.items;
          merged._consoleTotal=result.total;
          merged._consolePage=1;
        }
        onLoaded();
      });
    }
    /* Load first page of API calls */
    if(merged._apiCallsCount>0){
      pending++;
      fetchData(testId,'api-calls',1,500,function(err,result){
        if(!err&&result){
          merged.apiCalls=result.items;
          merged._apiCallsTotal=result.total;
          merged._apiCallsPage=1;
        }
        onLoaded();
      });
    }
  }

  /* ── Search (debounced) ── */
  var _searchTimer=0;
  function doSearch(q){
    clearTimeout(_searchTimer);
    _searchTimer=setTimeout(function(){searchQuery=q;applyFilters();},150);
  }
`;
